// Financial functions. fast-formula-parser ships 280 formulas and NOT ONE of
// them is financial — no NPV, no IRR, no PMT. A development proforma without
// IRR is not a proforma, so these come from @formulajs/formulajs (MIT) and are
// registered as custom functions.
//
// Verified against Excel on install:
//   NPV(0.1, -1000, 500, 500, 500) = 221.296…
//   IRR({-1000,500,500,500})       = 0.23375…
//   PMT(0.05/12, 360, 200000)      = -1073.643…

import * as F from '@formulajs/formulajs';
import { FormulaError } from 'fast-formula-parser';

interface WrappedArg {
  value: unknown;
  isArray: boolean;
  isRangeRef: boolean;
  isCellRef: boolean;
}

/** Flatten a range/array/scalar arg into the numbers it contains. */
function toNumbers(arg: WrappedArg | undefined): number[] {
  const out: number[] = [];
  const walk = (v: unknown): void => {
    if (Array.isArray(v)) {
      for (const x of v) walk(x);
      return;
    }
    if (typeof v === 'number' && Number.isFinite(v)) out.push(v);
    else if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v);
      if (Number.isFinite(n)) out.push(n);
    }
  };
  walk(arg?.value);
  return out;
}

function num(arg: WrappedArg | undefined, name: string, fallback?: number): number {
  const v = arg?.value;
  if (v === undefined || v === null || v === '') {
    if (fallback !== undefined) return fallback;
    throw new FormulaError('#VALUE!', `${name} is required`);
  }
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) throw new FormulaError('#VALUE!', `${name} must be a number`);
  return n;
}

/** Dates arrive as serial numbers or ISO strings; formulajs wants Date-ish. */
function toDates(arg: WrappedArg | undefined): unknown[] {
  const out: unknown[] = [];
  const walk = (v: unknown): void => {
    if (Array.isArray(v)) {
      for (const x of v) walk(x);
      return;
    }
    if (v === null || v === undefined || v === '') return;
    out.push(v);
  };
  walk(arg?.value);
  return out;
}

function guard<T>(fn: () => T): T {
  const result = fn();
  if (result instanceof Error) throw new FormulaError('#NUM!', result.message);
  if (typeof result === 'number' && !Number.isFinite(result)) throw FormulaError.NUM;
  return result;
}

export const FINANCE_FUNCTION_NAMES = [
  'NPV', 'IRR', 'PMT', 'XNPV', 'XIRR', 'FV', 'PV', 'NPER', 'RATE', 'IPMT', 'PPMT', 'MIRR',
] as const;

export function makeFinanceFunctions(): Record<string, (...args: never[]) => unknown> {
  const fns = {
    NPV: (...args: WrappedArg[]): number => {
      const rate = num(args[0], 'rate');
      const values = args.slice(1).flatMap(toNumbers);
      if (values.length === 0) throw new FormulaError('#VALUE!', 'NPV needs at least one cash flow');
      return guard(() => F.NPV(rate, ...values));
    },
    IRR: (...args: WrappedArg[]): number => {
      const values = toNumbers(args[0]);
      if (values.length < 2) throw new FormulaError('#NUM!', 'IRR needs at least two cash flows');
      if (!values.some((v) => v > 0) || !values.some((v) => v < 0)) {
        throw new FormulaError('#NUM!', 'IRR needs at least one positive and one negative cash flow');
      }
      return guard(() => F.IRR(values, args[1] ? num(args[1], 'guess', 0.1) : 0.1));
    },
    MIRR: (...args: WrappedArg[]): number =>
      guard(() => F.MIRR(toNumbers(args[0]), num(args[1], 'finance_rate'), num(args[2], 'reinvest_rate'))),
    PMT: (...args: WrappedArg[]): number =>
      guard(() => F.PMT(num(args[0], 'rate'), num(args[1], 'nper'), num(args[2], 'pv'), num(args[3], 'fv', 0), num(args[4], 'type', 0))),
    IPMT: (...args: WrappedArg[]): number =>
      guard(() => F.IPMT(num(args[0], 'rate'), num(args[1], 'per'), num(args[2], 'nper'), num(args[3], 'pv'), num(args[4], 'fv', 0), num(args[5], 'type', 0))),
    PPMT: (...args: WrappedArg[]): number =>
      guard(() => F.PPMT(num(args[0], 'rate'), num(args[1], 'per'), num(args[2], 'nper'), num(args[3], 'pv'), num(args[4], 'fv', 0), num(args[5], 'type', 0))),
    FV: (...args: WrappedArg[]): number =>
      guard(() => F.FV(num(args[0], 'rate'), num(args[1], 'nper'), num(args[2], 'pmt'), num(args[3], 'pv', 0), num(args[4], 'type', 0))),
    PV: (...args: WrappedArg[]): number =>
      guard(() => F.PV(num(args[0], 'rate'), num(args[1], 'nper'), num(args[2], 'pmt'), num(args[3], 'fv', 0), num(args[4], 'type', 0))),
    NPER: (...args: WrappedArg[]): number =>
      guard(() => F.NPER(num(args[0], 'rate'), num(args[1], 'pmt'), num(args[2], 'pv'), num(args[3], 'fv', 0), num(args[4], 'type', 0))),
    RATE: (...args: WrappedArg[]): number =>
      guard(() => F.RATE(num(args[0], 'nper'), num(args[1], 'pmt'), num(args[2], 'pv'), num(args[3], 'fv', 0), num(args[4], 'type', 0), num(args[5], 'guess', 0.1))),
    XNPV: (...args: WrappedArg[]): number =>
      guard(() => F.XNPV(num(args[0], 'rate'), toNumbers(args[1]), toDates(args[2]))),
    XIRR: (...args: WrappedArg[]): number =>
      guard(() => F.XIRR(toNumbers(args[0]), toDates(args[1]), args[2] ? num(args[2], 'guess', 0.1) : 0.1)),
  };
  return fns as unknown as Record<string, (...args: never[]) => unknown>;
}
